'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface NavigationItem {
  label: string;
  href: string;
  icon?: string;
}

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const navigationItems: NavigationItem[] = [
    { label: 'Home', href: '/homepage', icon: 'HomeIcon' },
    { label: 'Services', href: '/services', icon: 'CogIcon' },
    { label: 'Industries', href: '/industries', icon: 'BuildingOfficeIcon' },
    { label: 'About', href: '/about', icon: 'InformationCircleIcon' },
    { label: 'Contact', href: '/contact', icon: 'EnvelopeIcon' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-base ${
          isScrolled ? 'bg-card shadow-md' : 'bg-card'
        } ${className}`}
      >
        <div className="w-full">
          <div className="flex items-center justify-between h-16 px-4 md:px-6 lg:px-8">
            <Link
              href="/homepage"
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-base"
              onClick={closeMobileMenu}
            >
              <div className="relative w-32 h-32 md:w-40 md:h-40 flex items-center justify-center">
                <AppImage
                  // src="/assets/images/logo_venka-1767350987369.png"
                  src="/assets/images/venklogonew.PNG" 
                  alt="Invictosoft Logo"
                  className="w-full h-full object-contain drop-shadow-lg"
                  width={160}
                  height={160}
                  priority
                />
              </div>
            </Link>

            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="px-4 py-2 text-sm font-body font-semibold text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-base"
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="group relative">
                  <AppImage
                    src="/assets/images/Microsoft-Partner-1767340959771.png"
                    alt="Microsoft Partner"
                    width={80}
                    height={32}
                    className="h-8 w-auto object-contain opacity-80 hover:opacity-100 transition-all duration-base cursor-pointer group-hover:scale-105"
                  />
                  <div className="absolute inset-0 rounded-md shadow-partnership opacity-0 group-hover:opacity-100 transition-opacity duration-base pointer-events-none" />
                </div>
                <div className="group relative">
                  <AppImage
                    src="/assets/images/Screenshot_2026-01-02_at_12.41.30_PM-1767340820772.png"
                    alt="AWS Partner"
                    width={60}
                    height={32}
                    className="h-8 w-auto object-contain opacity-80 hover:opacity-100 transition-all duration-base cursor-pointer group-hover:scale-105"
                  />
                  <div className="absolute inset-0 rounded-md shadow-partnership opacity-0 group-hover:opacity-100 transition-opacity duration-base pointer-events-none" />
                </div>
              </div>
              <button className="px-6 py-2.5 bg-conversion text-conversion-foreground font-headline font-semibold text-sm rounded-md hover:bg-opacity-90 shadow-cta hover:shadow-partnership transition-all duration-base transform hover:-translate-y-0.5">
                Schedule Consultation
              </button>
            </div>

            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-base"
              aria-label="Toggle mobile menu"
            >
              <Icon
                name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                size={24}
                variant="outline"
              />
            </button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-background lg:hidden"
          style={{ top: '64px' }}
        >
          <nav className="flex flex-col h-full overflow-y-auto">
            <div className="flex-1 px-4 py-6 space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={closeMobileMenu}
                  className="flex items-center space-x-3 px-4 py-3 text-base font-body font-semibold text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-base"
                >
                  {item.icon && (
                    <Icon name={item.icon as any} size={20} variant="outline" />
                  )}
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>

            <div className="border-t border-border p-4 space-y-4">
              <div className="flex items-center justify-center space-x-4 pb-4">
                <AppImage
                  src="/assets/images/Microsoft-Partner-1767340959771.png"
                  alt="Microsoft Partner"
                  width={80}
                  height={32}
                  className="h-8 w-auto object-contain opacity-80"
                />
                <AppImage
                  src="/assets/images/Screenshot_2026-01-02_at_12.41.30_PM-1767340820772.png"
                  alt="AWS Partner"
                  width={60}
                  height={32}
                  className="h-8 w-auto object-contain opacity-80"
                />
              </div>
              <button
                onClick={closeMobileMenu}
                className="w-full px-6 py-3 bg-conversion text-conversion-foreground font-headline font-semibold text-base rounded-md hover:bg-opacity-90 shadow-cta transition-all duration-base"
              >
                Schedule Consultation
              </button>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;